// SPEC 8.2. OAuth 2.0 PKCE via ArcGISIdentityManager; only Portal administrators may proceed.
import { ArcGISIdentityManager } from "@esri/arcgis-rest-request";
import { getSelf } from "@esri/arcgis-rest-portal";
import type { AppConfig } from "./api/config";

const SESSION_KEY = "orgfilter.session";
const RETURN_KEY = "orgfilter.returnHash";

/** Raised when the signed-in user is not a Portal administrator. */
export class NotAnAdministratorError extends Error {
  constructor(readonly username: string, readonly role: string) {
    super("Administrator role required");
  }
}

export interface Me { username: string; fullName: string; role: string; orgId: string; portalName: string; }

/** The redirect URI registered for the app item: this page, without query or hash. */
function redirectUri(): string {
  return location.origin + location.pathname;
}

export function sharingRestUrl(cfg: AppConfig): string {
  return `${cfg.portalUrl.replace(/\/+$/, "")}/sharing/rest`;
}

/**
 * Resolves to a session, or never resolves because the browser is being sent to
 * the Portal's authorize page. The token is held in `sessionStorage` only, so it
 * dies with the tab (SPEC 8.2) — no refresh token is persisted anywhere else.
 */
export async function signIn(cfg: AppConfig): Promise<ArcGISIdentityManager> {
  const portal = sharingRestUrl(cfg);
  const options = { clientId: cfg.appId, redirectUri: redirectUri(), portal, pkce: true, popup: false };

  const restored = restore();
  if (restored) return restored;

  if (isRedirectLeg()) {
    const session = await ArcGISIdentityManager.completeOAuth2(options);
    remember(session);
    // Drop ?code=… from the address bar and put the user back where they were.
    const back = sessionStorage.getItem(RETURN_KEY) ?? "";
    sessionStorage.removeItem(RETURN_KEY);
    history.replaceState(null, "", redirectUri() + back);
    return session;
  }

  sessionStorage.setItem(RETURN_KEY, location.hash);
  await ArcGISIdentityManager.beginOAuth2(options);
  // beginOAuth2 with popup:false navigates away; nothing after this runs.
  return new Promise<ArcGISIdentityManager>(() => {});
}

function isRedirectLeg(): boolean {
  return new URLSearchParams(location.search).has("code") || location.hash.includes("access_token=");
}

function restore(): ArcGISIdentityManager | null {
  const stored = sessionStorage.getItem(SESSION_KEY);
  if (!stored) {
    console.info("[orgfilter] auth: nothing stored, starting a fresh sign-in");
    return null;
  }
  try {
    const session = ArcGISIdentityManager.deserialize(stored);
    if (!session.token) return null;

    // PKCE grants a short-lived access token (~20 minutes) alongside a long-lived refresh
    // token, and ArcGISIdentityManager renews the former on demand. So an expired access
    // token is not a reason to sign in again: only an exhausted refresh token is. Treating
    // the two alike sent the administrator back through the consent screen every 20 minutes.
    if (!expired(session.tokenExpires)) return session;
    if (session.canRefresh && !expired(session.refreshTokenExpires)) return session;

    console.info("[orgfilter] auth: stored session can no longer be renewed, signing in again");
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  } catch (e) {
    console.info(`[orgfilter] auth: stored entry unreadable (${(e as Error).message})`);
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  }
}

/** Treats "no expiry recorded" as still valid; a 60s margin covers a slow request in flight. */
function expired(at: Date | undefined | null): boolean {
  return !!at && at.getTime() <= Date.now() + 60_000;
}

function remember(session: ArcGISIdentityManager): void {
  try {
    sessionStorage.setItem(SESSION_KEY, session.serialize());
  } catch (e) {
    console.warn(`[orgfilter] auth: could not store the session (${(e as Error).message}); you will be asked to sign in again on every load`);
  }
}

export function signOut(): void {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  location.hash = "";
  location.reload();
}

/**
 * SPEC 8.2: `portals/self`; anything other than `org_admin` gets nothing. The
 * Manager's every action is an administrator action, so there is no read-only mode.
 */
export async function requireAdmin(session: ArcGISIdentityManager): Promise<Me> {
  const self = await getSelf({ authentication: session });
  const user = self.user ?? {};
  const me: Me = {
    username: user.username ?? session.username ?? "",
    fullName: user.fullName ?? user.username ?? "",
    role: user.role ?? "",
    orgId: self.id ?? "",
    portalName: self.name ?? self.portalName ?? ""
  };
  if (me.role !== "org_admin") throw new NotAnAdministratorError(me.username, me.role || "none");
  return me;
}
